import { useEffect, useRef, useState } from 'react';

import { Input } from '@@/form-components/Input';
import { Select } from '@@/form-components/ReactSelect';

import {
  ByteUnit,
  byteUnitMap,
  byteUnitOptions,
  convertBytesToHumanReadable,
  formatDisplayValue,
  isByteUnit,
} from './bytes';

interface ByteThresholdControlProps {
  value: number;
  onChange: (value: number) => void;
  id: string;
  name: string;
  onBlur?: () => void;
  disabled?: boolean;
  ariaDescribedBy?: string;
  className: string;
  inputClassName?: string;
  unitClassName: string;
}

export function ByteThresholdControl({
  value,
  onChange,
  id,
  name,
  onBlur,
  disabled,
  ariaDescribedBy,
  className,
  inputClassName = 'flex-1',
  unitClassName,
}: ByteThresholdControlProps) {
  const unitId = `${id}-unit`;

  const initialUnit = pickInitialUnit(value);
  const [selectedUnit, setSelectedUnit] = useState<ByteUnit>(initialUnit);
  const [displayValue, setDisplayValue] = useState<string>(() =>
    formatForUnit(value, initialUnit)
  );

  // Resync only when value changes from outside. Suppress the sync when the
  // change came from our own handleChange() so the user's unit choice and
  // in-progress decimal input aren't clobbered by the value round-trip.
  const emittedRef = useRef(value);

  useEffect(() => {
    if (value === emittedRef.current) return;
    emittedRef.current = value;
    if (value === 0) {
      setDisplayValue('0');
      return;
    }
    const { unit } = convertBytesToHumanReadable(value);
    setSelectedUnit(unit);
    setDisplayValue(formatForUnit(value, unit));
  }, [value]);

  return (
    <div className={className}>
      <div className={inputClassName}>
        <Input
          id={id}
          name={name}
          type="number"
          value={displayValue}
          onChange={(event) => handleDisplayValueChange(event.target.value)}
          onBlur={onBlur}
          placeholder="Enter value (e.g., 500)"
          data-cy={id}
          step="any"
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
        />
      </div>
      <div className={unitClassName}>
        <Select
          id={unitId}
          options={byteUnitOptions}
          value={byteUnitMap.get(selectedUnit)}
          onChange={(option) => {
            if (!option || !isByteUnit(option.value)) return;
            handleUnitChange(option.value);
          }}
          isSearchable={false}
          isDisabled={disabled}
          data-cy={unitId}
        />
      </div>
    </div>
  );

  function handleDisplayValueChange(nextDisplayValue: string) {
    setDisplayValue(nextDisplayValue);
    const parsedValue = parseFloat(nextDisplayValue);
    if (Number.isNaN(parsedValue)) {
      handleChange(0);
      return;
    }
    handleChange(Math.round(parsedValue * getMultiplier(selectedUnit)));
  }

  function handleUnitChange(nextUnit: ByteUnit) {
    setSelectedUnit(nextUnit);
    setDisplayValue(formatForUnit(emittedRef.current, nextUnit));
  }

  function handleChange(bytes: number) {
    emittedRef.current = bytes;
    onChange(bytes);
  }
}

// 'B' is the unit convertBytesToHumanReadable returns for 0, but MB is the
// expected default scale when the user starts entering a fresh threshold.
function pickInitialUnit(bytes: number): ByteUnit {
  return bytes === 0 ? 'MB' : convertBytesToHumanReadable(bytes).unit;
}

function getMultiplier(unit: ByteUnit): number {
  return byteUnitMap.get(unit)?.multiplier ?? 1;
}

function formatForUnit(bytes: number, unit: ByteUnit): string {
  if (bytes === 0) return '0';
  return formatDisplayValue(bytes / getMultiplier(unit));
}
